import { useState } from 'react';
import { mealsApi, type ReprocessResult } from '@/shared/api/endpoints';
import { shiftIsoDate, todayIso } from '@/shared/format/dates';
import { describeError } from '@/shared/hooks/useAsync';
import { Banner, Button, Card, Field } from '@/shared/ui';

/**
 * CU-11 · reprocesar un rango de fechas.
 *
 * Dos pasos y no uno: el sistema primero informa cuántos ítems tocaría (paso 3) y recién con eso
 * a la vista la persona confirma. Un botón único que reprocesa treinta días sin decir cuántos
 * ítems son pide una decisión a ciegas sobre algo que después no se deshace.
 *
 * Las correcciones del usuario quedan afuera del recuento porque nunca vuelven a resolverse
 * automáticamente (RN-06): reprocesar no le pisa a nadie lo que corrigió a mano.
 */
export function ReprocessPage() {
  const [from, setFrom] = useState(shiftIsoDate(todayIso(), -7));
  const [to, setTo] = useState(todayIso());
  const [preview, setPreview] = useState<ReprocessResult | null>(null);
  const [result, setResult] = useState<ReprocessResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const count = async () => {
    setBusy(true);
    setError(null);
    setResult(null);

    try {
      setPreview(await mealsApi.reprocess(from, to, false));
    } catch (cause) {
      setError(describeError(cause));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const execute = async () => {
    setBusy(true);
    setError(null);

    try {
      setResult(await mealsApi.reprocess(from, to, true));
      setPreview(null);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <h1>Volver a estimar</h1>
      <p className="muted small">
        Vuelve a pasar por el motor los ítems de un rango de fechas. Lo que corregiste a mano queda
        como está.
      </p>

      {error !== null && <Banner tone="danger">{error}</Banner>}

      <Card>
        <Field
          label="Desde"
          type="date"
          max={to}
          value={from}
          onChange={(event) => {
            setFrom(event.target.value);
            setPreview(null);
          }}
        />
        <Field
          label="Hasta"
          type="date"
          max={todayIso()}
          value={to}
          onChange={(event) => {
            setTo(event.target.value);
            setPreview(null);
          }}
        />

        <Button variant="quiet" block onClick={() => void count()} disabled={busy}>
          {busy && preview === null ? 'Contando…' : 'Ver cuántos son'}
        </Button>
      </Card>

      {preview !== null && (
        <Card>
          {preview.affected === 0 ? (
            <Banner tone="info">No hay ítems para reprocesar en ese rango.</Banner>
          ) : (
            <>
              <Banner tone="info">
                Se van a volver a estimar {preview.affected}{' '}
                {preview.affected === 1 ? 'ítem' : 'ítems'}.
              </Banner>
              <Button variant="primary" block onClick={() => void execute()} disabled={busy}>
                {busy ? 'Reprocesando…' : 'Reprocesar'}
              </Button>
            </>
          )}
        </Card>
      )}

      {result !== null && (
        <Card>
          <strong>Listo</strong>
          <p className="small">
            {result.changed} {result.changed === 1 ? 'ítem cambió' : 'ítems cambiaron'} de
            estimación y {result.unchanged} {result.unchanged === 1 ? 'quedó' : 'quedaron'} igual.
          </p>
        </Card>
      )}
    </div>
  );
}
